import type { GetStaticPaths, GetStaticProps } from 'next'
import type { ComponentProps } from 'react'

import { getAuthorBySlug } from '@/graphql/Author'
import { getAllAuthors } from '@/graphql/Authors'
import { getHealthAcademyArticleBySlug } from '@/graphql/HealthAcademyArticle'
import { allHealthAcademyArticles, getAllArticlesByAuthor } from '@/graphql/HealthAcademyArticles'
import { getTopAcademyCategories } from '@/graphql/HealthAcademyCategories'

import { AcademyContent } from '@/components/pages/learn/AcademyContentPage'

type Props = ComponentProps<typeof AcademyContent>
type PathParams = { slug: string }

export default AcademyContent

export const getStaticPaths: GetStaticPaths<PathParams> = async () => {
  const [articles, authors] = await Promise.all([allHealthAcademyArticles(), getAllAuthors()])
  const slugs: string[] = [...articles.map((article) => article.articleSlug), ...authors.map((author) => author.slug)]

  return { paths: slugs.map((slug) => `/learn/${slug}`), fallback: false }
}

export const getStaticProps: GetStaticProps<Props, PathParams> = async ({ params, preview }) => {
  const slug = params?.slug
  if (!slug) throw new Error('No slug provided')

  const [article, author] = await Promise.all([
    getHealthAcademyArticleBySlug(slug, preview),
    getAuthorBySlug(slug, preview),
  ])

  if (article && author) {
    throw new Error(`Duplicate slug for article and author: ${slug}`)
  }

  if (article) {
    const categories = await getTopAcademyCategories()
    return { props: { article, categories } }
  }

  if (author) {
    const articles = await getAllArticlesByAuthor(author.id, preview)
    const categories = await getTopAcademyCategories()
    return { props: { author, articles, categories } }
  }

  throw new Error(`No slug found for either article and author: ${slug}`)
}
